import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  LinearProgress,
  Grid,
} from '@mui/material';
import {
  getActiveSurvey,
  getYesNoSurveyResults,
} from './../../services/survey_services';
import { useAccount } from '../../contexts/Account';
import { countActivities } from '../../services/company_services';
import PrioritiesModal from './PrioritiesModal';
import eventBus from '../../eventBus';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';

const styles = {
  percentageText: {
    fontWeight: '600',
    fontSize: '18px',
  },
  countText: {
    fontWeight: '400',
    fontSize: '16px',
  },
  miniTileText: {
    fontWeight: '600',
    fontSize: 18,
    lineHeight: '24px',
    color: '#232B35',
  },
  miniTileCount: {
    fontWeight: '600',
    fontSize: '55px',
    color: '#232B35',
  },
  card: { borderRadius: 2, bgcolor: '#fafcff', cursor: 'pointer' },
};

export default function BoardStats() {
  const [singleQuestion, setSingleQuestion] = useState({ body: '' });
  const [loadingQuestion, setLoadingQuestion] = useState(true);
  const [yesNoSurveyResults, setYesNoSurveyResults] = useState({
    yesPercentage: 0,
    yesCount: 0,
    noPercentage: 0,
    noCount: 0,
  });
  const [loadingResults, setLoadingResults] = useState(true);
  const [activities, setActivities] = useState({
    activities: 0,
    ideas: 0,
  });
  const [prioritiesOpen, setPrioritiesOpen] = useState(false);
  const { accountDetails } = useAccount();
  const history = useHistory();
  const activeAdvisors = Array.from(
    new Map(
      accountDetails.boardAdvisors
        .filter((advisor) => advisor.advisorStatus === 'active')
        .map((advisor) => [advisor.id, advisor])
    ).values()
  );

  useEffect(async () => {
    let isMounted = true;
    const { data } = await getActiveSurvey({
      COMPANY_ID: accountDetails.id,
    });
    const questions = data.getActiveSurvey;
    if (questions && questions.length > 0 && isMounted) {
      setSingleQuestion(questions[0]);
      setLoadingQuestion(false);
      const response = await getYesNoSurveyResults({
        COMPANY_ID: accountDetails.id,
      });
      if (
        response &&
        response.data &&
        response.data.getYesNoSurveyResults &&
        isMounted
      ) {
        setYesNoSurveyResults(response.data.getYesNoSurveyResults);
      }
    } else {
      setSingleQuestion({
        body: 'No active survey found.',
      });
      setLoadingQuestion(false);
    }
    setLoadingResults(false);

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(async () => {
    let isMounted = true;
    const { data } = await countActivities({
      companyId: accountDetails.id
    });
    if (isMounted) {
      setActivities(data.countActivities || {
        activities: 0,
        ideas: 0,
      });
    }

    eventBus.on('surveyUpdated', (data) => {
      setSingleQuestion({ body: data.question });
      setYesNoSurveyResults({
        yesPercentage: 0,
        yesCount: 0,
        noPercentage: 0,
        noCount: 0,
      })
    });
    return () => {
      isMounted = false;
      eventBus.remove('surveyUpdated');
    };
  }, []);

  return (
    <Box sx={{ height: '100%' }}>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          py: { xxs: '22px', sm: '32px' },
          px: '20px',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mb: 2,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontWeight: '600',
              fontSize: '20px',
              lineHeight: '130%',
              color: '#001030',
            }}
          >
            <img
              src="/icons/board_stats.svg"
              alt="Board Stats"
              style={{
                width: '28px',
                height: '28px',
                verticalAlign: 'middle',
                marginRight: '8px',
                marginBottom: '2px',
              }}
            />
            Board Stats
          </Typography>
          <Typography
            sx={{
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '130%',
              color: '#232B35',
              cursor: 'pointer',
            }}
            onClick={() => setPrioritiesOpen(true)}
          >
            <Box
              component="img"
              src="/images/dart.png"
              alt="Dart Icon"
              sx={{
                width: '18px',
                height: '18px',
                verticalAlign: 'middle',
                mb: '6px',
                mr: '12px',
              }}
            />
            Update my priorities
          </Typography>
        </Box>
        <Card
          variant="outlined"
          sx={{ borderRadius: 2, bgcolor: '#fafcff', mb: 2 }}
        >
          <CardContent>
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '18px',
                lineHeight: '24px',
                color: '#232B35',
                mb: 2,
              }}
            >
              Survey Results
            </Typography>
            {loadingQuestion ? (
              <LoadingLogoSkeleton />
            ) : (
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: '16px',
                  lineHeight: '150%',
                  color: '#232B35',
                }}
              >
                {singleQuestion.body}
              </Typography>
            )}
            <Box sx={{ width: '100%' }}>
              <Box display={'flex'} justifyContent="space-between" mt={2}>
                <Typography sx={styles.percentageText}>Yes</Typography>
                <Typography sx={styles.percentageText}>
                  {yesNoSurveyResults.yesPercentage}%{' '}
                  <span style={styles.countText}>
                    ({yesNoSurveyResults.yesCount})
                  </span>
                </Typography>
              </Box>
              <LinearProgress
                variant={loadingResults ? 'indeterminate' : 'determinate'}
                value={yesNoSurveyResults.yesPercentage}
                sx={{
                  height: '12px',
                  borderRadius: '6px',
                  backgroundColor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2B71F6',
                  },
                }}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <Box display={'flex'} justifyContent="space-between" mt={2}>
                <Typography sx={styles.percentageText}>No</Typography>
                <Typography sx={styles.percentageText}>
                  {yesNoSurveyResults.noPercentage}%{' '}
                  <span style={styles.countText}>
                    ({yesNoSurveyResults.noCount})
                  </span>
                </Typography>
              </Box>
              <LinearProgress
                variant={loadingResults ? 'indeterminate' : 'determinate'}
                value={yesNoSurveyResults.noPercentage}
                sx={{
                  height: '12px',
                  borderRadius: '6px',
                  backgroundColor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4FB5F9',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
        <Grid container spacing={2}>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/advisors');
              }}
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activeAdvisors.length}
                </Typography>
                <Typography sx={styles.miniTileText}>Advisors</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/advisors');
              }}
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activities.ideas}
                </Typography>
                <Typography sx={styles.miniTileText}>Ideas</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xxs={12} xs={4}>
            <Card
              variant="outlined"
              sx={styles.card}
              onClick={() => {
                history.push('/advisors');
              }}
            >
              <CardContent>
                <Typography sx={styles.miniTileCount}>
                  {activities.activities}
                </Typography>
                <Typography sx={styles.miniTileText}>Board Activity</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      <PrioritiesModal
        open={prioritiesOpen}
        setIsOpen={setPrioritiesOpen}
        survey={singleQuestion}
      />
    </Box>
  );
}
